import { analytics } from '../../services/analytics';
import type { SlotBattleEventName, SlotBattlePopupEventName } from '../../services/analytics/types';

import { selectBattleRivePrize, selectBattleRiveRewardArt } from './battleRive';
import type { SlotBattle } from './types';

/**
 * Battle of the Slots analytics (golden spec `battle_of_the_slots`).
 *
 * ## The taxonomy
 * `event_parent: tournament`. The category follows the popup-vs-page split: the opening, the result
 * popup and their CTAs are `slot_battle_popup`; the entry widget and everything on the battle page
 * (standings, leaderboard, info overlay, close) are `slot_battle_page`. The category is derived from
 * the event name here, so a call site never picks it.
 *
 * ## `battle_id`
 * On every event, and the only new column: prize pool, contenders, shares, rank, qualification and
 * payout all join from the backend on it, so none of them is sent. A segment key is unique only within
 * its run, so the id is `<runKey>:<segmentKey>` — the same composite core uses for the battle's Redis
 * hash-tag slot.
 *
 * ## Not tracked (spec)
 * The leaderboard gift tooltip, the timer, the page dots, the counting pane, the claim animation, the
 * legacy nav-rail icon, and no claim-result event: the claim's outcome is backend truth
 * (`battle_participant.claimed_at` + the wallet transaction).
 */

const POPUP_EVENTS = new Set<SlotBattleEventName>([
  'opening_popup',
  'lets_go_button',
  'result_popup',
  'claim_button',
  'got_it_button',
] satisfies SlotBattlePopupEventName[]);

/** `button_text`, lowercased verbatim from the Figma copy — the curly apostrophe included. */
export const SLOT_BATTLE_BUTTON_TEXT = {
  letsGo: 'let’s go',
  claim: 'claim',
  gotIt: 'got it',
} as const;

export type SlotBattlePrizeType = 'prize_pool' | 'grand_reward';
export type SlotBattleOutcomeValue = 'won' | 'lost_with_reward' | 'lost_no_reward' | 'unknown';

const KNOWN_OUTCOMES = new Set<string>(['won', 'lost_with_reward', 'lost_no_reward']);

export const slotBattleId = (battle: Pick<SlotBattle, 'runKey' | 'segmentKey'>): string =>
  `${battle.runKey}:${battle.segmentKey}`;

/**
 * The prize panel header the opening shows: the `GRAND_PRIZE` row when the prize has bundle items the
 * row can draw (free spins among them), else the coins-only `PRIZE_POOL` panel. Same rule as
 * `buildBattleRiveData`.
 */
export const openingPrizeType = (battle: SlotBattle): SlotBattlePrizeType =>
  selectBattleRiveRewardArt(selectBattleRivePrize(battle, 'opening').bundleItems).length > 0
    ? 'grand_reward'
    : 'prize_pool';

/** The backend outcome, with `unknown` for anything this client does not know. */
export const battleOutcomeValue = (outcome: string | null | undefined): SlotBattleOutcomeValue =>
  outcome && KNOWN_OUTCOMES.has(outcome) ? (outcome as SlotBattleOutcomeValue) : 'unknown';

type TrackProps = {
  event_action: 'impression' | 'click';
  click_location?: 'lobby' | 'in_slot';
  event_type?: 'prize_type' | 'battle_outcome' | 'slot_game';
  event_value?: string;
  button_text?: string;
};

export function trackSlotBattle(
  eventName: SlotBattleEventName,
  battle: Pick<SlotBattle, 'runKey' | 'segmentKey'>,
  props: TrackProps,
): void {
  void analytics.sendSlotBattleEvent(eventName, {
    event_parent: 'tournament',
    event_category: POPUP_EVENTS.has(eventName) ? 'slot_battle_popup' : 'slot_battle_page',
    page_url: window.location.href,
    battle_id: slotBattleId(battle),
    ...props,
  });
}
